// src/App.js

import React, { useRef } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { ScrapingProvider } from './context/ScrapingContext';
import ToastProvider from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import { Crown, Loader2 } from 'lucide-react';

import HomePage from './pages/HomePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import OAuthReturnPage from './pages/OAuthReturnPage';
import ResubscribePage from './pages/ResubscribePage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import SupportPage from './pages/SupportPage';
import DocsPage from './pages/DocsPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import MainApp from './MainApp';
import { useSubscription } from './context/SubscriptionContext';

// Full-screen Stripe redirect overlay
const StripeRedirectScreen = () => (
  <div className="fixed inset-0 z-[200] flex items-center justify-center bg-zinc-950">
    <div className="text-center">
      <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-6">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center animate-pulse">
          <Crown className="w-7 h-7 text-white" />
        </div>
      </div>
      <h2 className="text-2xl font-bold text-white mb-3">Account Created!</h2>
      <p className="text-zinc-400 mb-8">
        Redirecting you to secure checkout...
      </p>
      <div className="flex items-center justify-center gap-3 text-amber-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="font-medium">Connecting to Stripe</span>
      </div>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SubscriptionProvider>
          <SettingsProvider>
            <ScrapingProvider>
              <ToastProvider />
              <OfflineBanner />
              <AppRoutes />
            </ScrapingProvider>
          </SettingsProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

function AppRoutes() {
  const { currentUser, loading, mfaRequired, mfaChecking, redirectingToCheckout } = useAuth();
  const { subscriptionStatus, loading: subLoading } = useSubscription();

  // Track if we've ever shown MainApp - once shown, never unmount it for loading states
  // This prevents modals from closing when tab switching triggers auth/subscription refreshes
  const hasShownMainAppRef = useRef(false);

  // Preview mode for Stripe redirect screen - add ?preview=stripe to URL
  const urlParams = new URLSearchParams(window.location.search);
  const previewStripe = urlParams.get('preview') === 'stripe';

  if (previewStripe) {
    return <StripeRedirectScreen />;
  }

  // Reset password page is always accessible (regardless of auth state)
  // Check URL path to show reset password page
  const isResetPasswordPage = window.location.pathname === '/reset-password';
  if (isResetPasswordPage) {
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Routes>
    );
  }

  // OAuth return page (Gmail connect popup/full-page) — must render
  // instantly regardless of auth/loading state so the popup can broadcast
  // its result and close without booting the whole app experience.
  if (window.location.pathname === '/oauth-return') {
    return (
      <Routes>
        <Route path="/oauth-return" element={<OAuthReturnPage />} />
      </Routes>
    );
  }

  // Show Stripe redirect screen - prevents dashboard flash during Pro signup
  if (redirectingToCheckout) {
    return <StripeRedirectScreen />;
  }

  // Show loading state while checking auth or MFA status (only on initial load)
  if ((loading || mfaChecking) && !hasShownMainAppRef.current) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-zinc-300 border-t-blue-600" />
          <p className="text-sm text-zinc-600 dark:text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }

  // If not logged in OR if MFA is required, show HomePage (which contains AuthForm with MFA handling)
  // This prevents the dashboard flash
  if (!currentUser || mfaRequired) {
    return (
      <Routes>

        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/*" element={<HomePage />} />
      </Routes>
    );
  }

  // Show loading while checking subscription status (only on initial load)
  if (subLoading && !hasShownMainAppRef.current) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-zinc-300 border-t-blue-600" />
          <p className="text-sm text-zinc-600 dark:text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if user needs to resubscribe (canceled or past due paid subscription)
  // Note: Free plan users are NOT blocked - they can use the app with limits
  if (subscriptionStatus.needsResubscribe) {
    return (
      <Routes>
        <Route path="/*" element={<ResubscribePage />} />
      </Routes>
    );
  }

  // Mark that we've shown MainApp - never show loading spinners again after this
  hasShownMainAppRef.current = true;

  // Fully authenticated (no MFA pending)
  return (
    <Routes>
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/welcome" element={<HomePage />} />
      <Route path="/*" element={<MainApp />} />
    </Routes>
  );
}

export default App;